import InvoiceAutomationJobMonthRuleEnum from '../Enums/InvoiceAutomationJobMonthRuleEnum';
import InvoiceAutomationRunScheduleEnum from '../Enums/InvoiceAutomationRunScheduleEnum';
import InvoiceAutomationPlaceholderEnum from '../Enums/InvoiceAutomationPlaceholderEnum';
import ClientInvoiceReportOrderOptionsArray from '../Enums/ClientInvoiceReportOrderOptionsArray';
import ClientInvoiceReportGroupOptionsArray from '../Enums/ClientInvoiceReportGroupOptionsArray';
import selectHelper from './selectHelper';
import stringHelper from './stringHelper';
import { formatMonthLabel } from './invoiceAutomationMonthHelper';
import { formatProjectLabel } from './invoiceAutomationEditorHelper';

// The texts of a project's automatic invoicing setup page (InvoiceAutomationSetupPage), worked out from the setup.

// The line beside the status chip.
export function getSetupStatusText(isNewSetup: boolean, isActive: boolean, runSchedule?: string | null) {
  if (isNewSetup) {
    return 'Automatic invoicing is not set up for this project.';
  }
  if (!isActive) {
    return "Bravo leaves this project's jobs alone until someone activates it.";
  }
  if (runSchedule === InvoiceAutomationRunScheduleEnum.OnDemand) {
    return "Bravo adds this project's ready jobs to its drafts when someone clicks Run Now.";
  }
  return "Bravo adds this project's ready jobs to its drafts automatically, every morning.";
}

// The line at the top of the tab of a project on another setup's invoice, which has no setup of its own.
export function getInvoicedWithStatusText(invoicedWithProjectId: number, invoicedWithProjectName?: string | null) {
  const invoicedWithProject = formatProjectLabel(invoicedWithProjectId, invoicedWithProjectName);
  return `The automatic invoicing of project ${invoicedWithProject} invoices this project's jobs.`;
}

// The question Activate or Deactivate asks before it changes the status.
export function getActivationQuestion(isActive: boolean, runSchedule?: string | null) {
  if (!isActive) {
    return (
      "Deactivate automatic invoicing for this project? Bravo stops adding jobs to the project's drafts; the drafts " +
      'it made stay as they are.'
    );
  }
  if (runSchedule === InvoiceAutomationRunScheduleEnum.OnDemand) {
    return (
      "Activate automatic invoicing for this project? Bravo then adds the project's ready jobs to its drafts when " +
      'someone clicks Run Now.'
    );
  }
  return (
    "Activate automatic invoicing for this project? From the next morning on, Bravo adds the project's ready jobs " +
    'to its drafts automatically, every morning.'
  );
}

// The note under the sentence that picks when Bravo runs: that a setup running every morning needs nobody to click
// anything, and that one running on demand never runs by itself.
export function getRunScheduleNote(runSchedule?: string | null) {
  if (runSchedule === InvoiceAutomationRunScheduleEnum.OnDemand) {
    return 'Bravo never runs on its own; the drafts are made and updated only when someone clicks Run Now.';
  }
  return (
    'Nobody needs to click anything. Run Now is optional: it updates the drafts straight away, without waiting ' +
    'for the morning.'
  );
}

// The note under the sentence that picks a job's month.
export function getJobMonthNote(jobMonthRule?: string | null) {
  if (jobMonthRule === InvoiceAutomationJobMonthRuleEnum.PrismRunStart) {
    return "A shop of a September run dated 2 October goes on September's draft; a job without a run goes by its shop date.";
  }
  if (jobMonthRule === InvoiceAutomationJobMonthRuleEnum.DateAdded) {
    return (
      'One draft at a time: ready jobs are added to the open draft until it is finalized, even after its month ' +
      'has ended. The next ready job then starts a new draft.'
    );
  }
  return "A shop dated 2 October goes on October's draft.";
}

// One month under the setup's rules, for the timeline of step 1, in the order things happen: a person finalizes the
// draft before a late job can come. The highlighted moment is the one people get wrong: what happens to a job that
// comes after its month's draft was finalized.
export function getMonthTimeline(jobMonthRule?: string | null, runSchedule?: string | null) {
  const runTitle = runSchedule === InvoiceAutomationRunScheduleEnum.OnDemand ? 'Run Now' : 'Every morning';
  const checkEvent = { title: 'A person checks', text: 'The draft is checked and finalized, as today.' };
  if (jobMonthRule === InvoiceAutomationJobMonthRuleEnum.DateAdded) {
    return [
      { title: 'A job is ready', text: 'With no draft open, Bravo makes one.' },
      { title: runTitle, text: 'Every job that is ready goes on the open draft.' },
      checkEvent,
      {
        title: 'The next job',
        text: 'Once the draft is finalized, the next job that is ready starts a new draft.',
        isHighlighted: true,
      },
    ];
  }
  return [
    { title: 'A new month', text: "Its first ready job makes its draft. Last month's draft stays open." },
    { title: runTitle, text: 'Each ready job goes on the draft of its month.' },
    checkEvent,
    {
      title: 'A job comes late',
      text: 'A job of a month that is already finalized goes on the next open draft.',
      isHighlighted: true,
    },
  ];
}

// How the one invoice of the MVP is made, for step 2.
export function getHowOneInvoiceWorks(jobMonthRule?: string | null, runSchedule?: string | null) {
  const firstStep =
    jobMonthRule === InvoiceAutomationJobMonthRuleEnum.DateAdded
      ? 'When a job of this project is ready and no draft is open, Bravo makes one.'
      : "The first time a job of this project is ready for a month, Bravo makes that month's draft.";
  const addStep =
    runSchedule === InvoiceAutomationRunScheduleEnum.OnDemand
      ? 'Each time someone clicks Run Now, Bravo adds the jobs that have become ready to it, and takes off any that ' +
        'are no longer ready.'
      : 'Every morning, Bravo automatically adds the jobs that have become ready to it, and takes off any that are ' +
        'no longer ready.';
  return [
    firstStep,
    addStep,
    'The draft always gets the same bill-to contact, PO number, currency and invoice form.',
    'A person checks the draft and finalizes it, as today. A job a person takes off the draft is not added again, ' +
      'unless someone brings it back in with Run Now.',
  ];
}

// The first of the month of a date, as yyyy-MM-dd, the form the setup's months take.
export function getMonthIso(date: Date) {
  return `${date.getFullYear()}-${stringHelper.addLeadingZero(date.getMonth() + 1)}-01`;
}

// A reference or comment as a draft of the month gets it: {Month} and {Year} filled in, as Bravo does.
export function fillTemplate(template?: string | null, isoMonth?: string | null) {
  if (!template) {
    return template;
  }
  const [monthName, year] = formatMonthLabel(isoMonth).split(' ');
  return template
    .split(InvoiceAutomationPlaceholderEnum.Month)
    .join(monthName)
    .split(InvoiceAutomationPlaceholderEnum.Year)
    .join(year);
}

// Which jobs go on the invoice, as its card says it: this project's ready jobs, or those of every project on the
// invoice once others are on it (the card names them at its foot).
export function getJobsOnInvoiceText(setup) {
  return (setup.OtherProjectIds ?? []).length === 0
    ? "All of this project's jobs that are ready for invoicing"
    : 'All jobs of the projects on this invoice that are ready for invoicing';
}

// The other projects on the invoice, each named "#id name" from the client's projects the setup carries.
export function getOtherProjectLabels(setup) {
  const clientProjects = setup.ClientProjects ?? [];
  return (setup.OtherProjectIds ?? []).map((projectId) => {
    const clientProject = clientProjects.find((candidate) => candidate.projectId === projectId);
    return formatProjectLabel(projectId, clientProject?.projectName);
  });
}

// The payment terms and the invoice layout, the settings that are rarely changed, in one line for the foot of the
// invoice card.
export function getPaymentTermsAndLayoutSummary(setup) {
  const parts = [`${setup.DaysToPay} days to pay`, `${setup.LatePaymentInterestRate}% late fee`];
  const order = selectHelper.getOptionLabel(ClientInvoiceReportOrderOptionsArray, setup.ReportOrder).trim();
  if (order) {
    parts.push(`ordered by ${order}`);
  }
  const groups = [setup.ReportGroup1, setup.ReportGroup2]
    .map((group) => selectHelper.getOptionLabel(ClientInvoiceReportGroupOptionsArray, group).trim())
    .filter((groupLabel) => groupLabel !== '');
  if (groups.length > 0) {
    parts.push(`grouped by ${groups.join(', then ')}`);
  }
  return parts.join(' · ');
}
